import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; 

function VoucherDetails({ voucherId, startDate, endDate }) {
    const [voucher, setVoucher] = useState(null);
    const [entries, setEntries] = useState([]);

    const token = localStorage.getItem("accessToken");

    useEffect(() => {
        api.get(`/vouchers/${voucherId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
            setVoucher(response.data.voucher);
            setEntries(response.data.entries);
        })
        .catch((error) => {
            console.error("Error fetching voucher details:", error);
        });
    }, [voucherId]);

    if (!voucher) return <p>Loading…</p>;

    const totalDebit = entries.reduce(
        (a, b) => a + parseFloat(b.debit || 0),
        0
    );
    const totalCredit = entries.reduce(
        (a, b) => a + parseFloat(b.credit || 0),
        0
    );

    const exportPDF = () => {
        const doc = new jsPDF({ unit: "pt", format: "A4" });

        const pageWidth = doc.internal.pageSize.getWidth();
        const centerX = pageWidth / 2;

        // ---- HEADER ----
        doc.setFontSize(14);
        doc.text(`${voucher.branch?.company?.name || ""} (${voucher.branch?.name || ""})`, 595 / 2, 40, { align: "center" });
       
        doc.setFontSize(12);
        doc.text("GENERAL JOURNAL", centerX, 60, { align: "center" });

        doc.setFontSize(10);
        doc.text(
            `For the Period From ${new Date(startDate).toLocaleDateString("en-GB")} To ${new Date(endDate).toLocaleDateString("en-GB")}`,   
            centerX,
            80,
            { align: "center" }
        );

        // ---- TOP LABEL AREA ----
        doc.setFontSize(10);

        const labelWidth = 74;
        const startX = 40;
        let currentY = 110;

        const addLabelRow = (label, value) => {
            doc.setFont("helvetica", "bold");
            doc.text(label, startX, currentY);

            doc.setFont("helvetica", "normal");
            doc.text(String(value || ""), startX + labelWidth, currentY);

            currentY += 18;
        };

        addLabelRow("Reference No:", voucher.series_no);
        addLabelRow(
            "Date:",
            new Date(voucher.voucher_date).toLocaleDateString("en-GB")
        );
        addLabelRow("Narration:", voucher.narration || "");

        // ---- TABLE BODY ----
        const rows = entries.map((item) => [
            `${item.ledger?.ledger_code || ""} - ${item.ledger?.ledger_name || ""}`,
            item.description || "",
            {
            content: item.debit ? parseFloat(item.debit).toFixed(2) : "",
            styles: { halign: "right" }
            },
            {
            content: item.credit ? parseFloat(item.credit).toFixed(2) : "",
            styles: { halign: "right" }
            }
        ]);

        // ---- TOTAL ROW (Right aligned + Bold) ----
        rows.push([
            "",
            { content: "TOTAL", styles: { fontStyle: "bold", halign: "right" } },
            {
            content: totalDebit.toFixed(2),
            styles: { fontStyle: "bold", halign: "right" }
            },
            {
            content: totalCredit.toFixed(2),
            styles: { fontStyle: "bold", halign: "right" }
            }
        ]);

        // ---- TABLE ----
        autoTable(doc, {
            startY: currentY + 10,
            head: [["Account", "Description", "Debit", "Credit"]],
            body: rows,
            styles: { fontSize: 10 },
            headStyles: {
            fillColor: [230, 230, 230],
            textColor: 20,
            fontStyle: "bold",
            },
            theme: "grid",
            margin: { left: 40, right: 40 },
            tableLineColor: 200,
            tableLineWidth: 0.25,
            columnStyles: {
            2: { halign: "right" }, // Debit column
            3: { halign: "right" }  // Credit column
            }
        });

        doc.save(`Voucher_${voucher.series_no || voucher.id}.pdf`);
    };

return (
    <div className="voucher-details p-2">

            <div className="voucher-top-header text-center mb-3">
                    <h4 className="company-name mb-0">
                            {voucher.branch?.company?.name} ({voucher.branch?.name})
                    </h4>

                    <h6 className="document-title mt-1">
                            General Journal
                    </h6>

                    <p className="document-period">
                        For the Period From &nbsp; 
                        {new Date(startDate).toLocaleDateString("en-GB")} 
                        &nbsp; To &nbsp;
                        {new Date(endDate).toLocaleDateString("en-GB")} 
                    </p>
            </div>

            <div className="d-flex justify-content-end gap-2 mb-2">
                    <button className="btn btn-sm btn-danger" onClick={exportPDF}>
                            Export PDF
                    </button>

                    {/* <button className="btn btn-sm btn-success" onClick={exportExcel}>
                            Export Excel
                    </button> */}
            </div>

            <div className="voucher-header-box mb-3 p-2 rounded">
                    <div className="vh-row">
                            <span className="vh-label">Reference No:</span>
                            <span className="vh-value">{voucher.series_no}</span>
                    </div>

                    <div className="vh-row">
                            <span className="vh-label">Date:</span>
                            <span className="vh-value">
                            {new Date(voucher.voucher_date).toLocaleDateString("en-GB")}
                            </span>
                    </div>

                    <div className="vh-row">
                            <span className="vh-label">Narration:</span>
                            <span className="vh-value">{voucher.narration}</span>
                    </div>
            </div>

            <table className="table table-bordered table-sm voucher-table">
                    <thead>
                    <tr>
                            <th width="15%">Account</th>
                            <th width="45%">Description</th>
                            <th width="10%" className="text-end">Debit</th>
                            <th width="10%" className="text-end">Credit</th>
                    </tr>
                    </thead>

                    <tbody>
                    {entries.map((row, idx) => (
                            <tr key={idx}>
                            <td width="30%">{row.ledger.ledger_code} - {row.ledger.ledger_name}</td>
                            <td width="50%">{row.description}</td>
                            <td className="text-end">
                                    {row.debit ? parseFloat(row.debit).toFixed(2) : ""}
                            </td>
                            <td className="text-end">
                                    {row.credit ? parseFloat(row.credit).toFixed(2) : ""}
                            </td>
                            </tr>
                    ))}

                    <tr>
                            <td colSpan="2" className="fw-bold text-end">Total</td>
                            <td className="fw-bold text-end">{totalDebit.toFixed(2)}</td>
                            <td className="fw-bold text-end">{totalCredit.toFixed(2)}</td>
                    </tr>
                    </tbody>
            </table>

    </div>
);
}

export default VoucherDetails;